/** Hooks de dados. Toda leitura passa pelo TanStack Query; escrita invalida a chave. */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import { TEMPO_REFERENCIA } from "@/lib/queryClient";
import type {
  Documento,
  EnvioAprovacao,
  Decisao,
  EnvioAprovacaoDetalhe,
  ExecucaoColeta,
  ExtracaoAto,
  FluxoSalvo,
  IndiceFiscal,
  PedidoCalculo,
  Produto,
  LotePropostas,
  TipoAto,
  ReferenciaModelos,
  ReferenciaUfs,
  ReferenciaUnidades,
  ResultadoOperacao,
  SubstituicoesContabil,
  SubstituicoesNfs,
  Terminal,
  Unidade,
} from "@/types";

const referencia = <T,>(caminho: string, chave: string[]) =>
  ({
    queryKey: chave,
    queryFn: () => api<T>(caminho),
    staleTime: TEMPO_REFERENCIA,
  }) satisfies UseQueryOptions<T>;

export const useUfs = () => useQuery(referencia<ReferenciaUfs>("/referencia/ufs", ["ufs"]));

export const useMatriz = () =>
  useQuery(
    referencia<{ ufs: string[]; matriz: Record<string, Record<string, number>> }>(
      "/referencia/matriz",
      ["matriz"],
    ),
  );

export const useTerminais = () =>
  useQuery(referencia<Terminal[]>("/referencia/terminais", ["terminais"]));

export const useUnidades = () =>
  useQuery(referencia<ReferenciaUnidades>("/referencia/unidades", ["unidades"]));

export const useModelos = () =>
  useQuery(referencia<ReferenciaModelos>("/referencia/modelos", ["modelos"]));

/** Roteiro fiscal + contábil + resumo + P&L do grafo atual. */
export function useCalculo(pedido: PedidoCalculo | null) {
  return useQuery({
    queryKey: ["calculo", pedido],
    queryFn: () => api<ResultadoOperacao>("/fiscal/calcular", { method: "POST", body: pedido }),
    enabled: Boolean(pedido && pedido.nodes.length),
    placeholderData: (anterior) => anterior, // evita piscar a tabela a cada ajuste
    staleTime: 5 * 60_000,
  });
}

export function useFluxos(contexto?: string) {
  return useQuery({
    queryKey: ["fluxos", contexto ?? "todos"],
    queryFn: () => api<FluxoSalvo[]>(`/fluxos${contexto ? `?contexto=${contexto}` : ""}`),
  });
}

export function useRemoverFluxo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/fluxos/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fluxos"] }),
  });
}

/** Operações que o usuário logado enviou para aprovação. */
export function useMinhasOperacoes() {
  return useQuery({
    queryKey: ["aprovacoes"],
    queryFn: () => api<EnvioAprovacao[]>("/aprovacoes"),
  });
}

export function useEnviarParaAprovacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pedido: PedidoCalculo & { variante?: string }) =>
      api<EnvioAprovacao>("/aprovacoes", { method: "POST", body: pedido }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aprovacoes"] }),
  });
}

export function useEnvio(id: string | undefined) {
  return useQuery({
    queryKey: ["aprovacoes", id],
    queryFn: () => api<EnvioAprovacaoDetalhe>(`/aprovacoes/${id}`),
    enabled: Boolean(id),
  });
}

/** Repositório TAX: envios de todos os usuários, com as três aprovações (filtro na tela). */
export function useRepositorioAprovacoes() {
  return useQuery({
    queryKey: ["aprovacoes", "todos"],
    queryFn: () => api<EnvioAprovacao[]>("/aprovacoes/todos"),
  });
}

/** Envios que o grupo do usuário ainda não classificou — só para quem aprova. */
export function usePendentes(habilitado: boolean) {
  return useQuery({
    queryKey: ["aprovacoes", "pendentes"],
    queryFn: () => api<EnvioAprovacao[]>("/aprovacoes/pendentes"),
    enabled: habilitado,
  });
}

export function useClassificar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { decisao: Decisao; comentario?: string }) =>
      api<EnvioAprovacao>(`/aprovacoes/${id}/classificacao`, { method: "PUT", body: corpo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aprovacoes"] }),
  });
}

/** Time Tax: grava a ordem de emissão das NFs de um envio (e o PDF é refeito). */
export function useReordenarNfsEnvio(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ordemNfs: string[]) =>
      api<EnvioAprovacaoDetalhe>(`/aprovacoes/${id}/ordem-nfs`, {
        method: "PUT",
        body: { ordemNfs },
      }),
    onSuccess: (envio) => qc.setQueryData(["aprovacoes", id], envio),
  });
}

/**
 * Time Tax: grava as correções do roteiro fiscal de um envio (o mapa inteiro).
 * A tela muda na hora e volta ao que estava se o servidor recusar.
 */
export function useCorrigirValoresEnvio(id: string) {
  const qc = useQueryClient();
  const chave = ["aprovacoes", id];
  return useMutation({
    mutationFn: (substituicoesNfs: SubstituicoesNfs) =>
      api<EnvioAprovacaoDetalhe>(`/aprovacoes/${id}/substituicoes-nfs`, {
        method: "PUT",
        body: { substituicoesNfs },
      }),
    onMutate: async (substituicoesNfs) => {
      await qc.cancelQueries({ queryKey: chave });
      const anterior = qc.getQueryData<EnvioAprovacaoDetalhe>(chave);
      if (anterior) {
        qc.setQueryData(chave, {
          ...anterior,
          pedido: { ...anterior.pedido, substituicoesNfs },
        });
      }
      return { anterior };
    },
    onError: (_erro, _subs, contexto) => {
      if (contexto?.anterior) qc.setQueryData(chave, contexto.anterior);
    },
    onSuccess: (envio) => qc.setQueryData(chave, envio),
  });
}

/** Time Contábil: grava as correções do roteiro contábil de um envio (o mapa inteiro). */
export function useCorrigirContabilEnvio(id: string) {
  const qc = useQueryClient();
  const chave = ["aprovacoes", id];
  return useMutation({
    mutationFn: (substituicoesContabil: SubstituicoesContabil) =>
      api<EnvioAprovacaoDetalhe>(`/aprovacoes/${id}/substituicoes-contabil`, {
        method: "PUT",
        body: { substituicoesContabil },
      }),
    onMutate: async (substituicoesContabil) => {
      await qc.cancelQueries({ queryKey: chave });
      const anterior = qc.getQueryData<EnvioAprovacaoDetalhe>(chave);
      if (anterior) {
        qc.setQueryData(chave, {
          ...anterior,
          pedido: { ...anterior.pedido, substituicoesContabil },
        });
      }
      return { anterior };
    },
    onError: (_erro, _subs, contexto) => {
      if (contexto?.anterior) qc.setQueryData(chave, contexto.anterior);
    },
    onSuccess: (envio) => qc.setQueryData(chave, envio),
  });
}

export function useRemoverEnvio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/aprovacoes/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aprovacoes"] }),
  });
}

export function useDocumentos(unidade: string | null) {
  return useQuery({
    queryKey: ["documentos", unidade],
    queryFn: () => api<{ unidade: Unidade; documentos: Documento[] }>(`/documentos/${unidade}`),
    enabled: Boolean(unidade),
  });
}

export function useAtualizarDocumento(unidade: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...campos }: Partial<Documento> & { id: string }) =>
      api<Documento>(`/documentos/item/${id}`, { method: "PUT", body: campos }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documentos", unidade] }),
  });
}

/** Diz se há provedor de LLM configurado no servidor (e qual modelo responde). */
export function useAssistenteStatus() {
  return useQuery({
    queryKey: ["assistente-status"],
    queryFn: () => api<{ habilitado: boolean; modelo: string }>("/assistente/status"),
    staleTime: TEMPO_REFERENCIA,
  });
}

export function useMontarPorTexto() {
  return useMutation({
    mutationFn: (dados: { descricao: string; contexto: string }) =>
      api<{ nodes: unknown[]; edges: unknown[]; produto?: Produto }>("/assistente/montar", {
        method: "POST",
        body: dados,
      }),
  });
}

export function useTranscrever() {
  return useMutation({
    mutationFn: (dados: { audio: string; formato: string }) =>
      api<{ texto: string }>("/assistente/transcrever", { method: "POST", body: dados }),
  });
}

/** ---------------- Índices fiscais variáveis (Base TAX → Índices fiscais) ---------------- */

export function useIndicesFiscais() {
  return useQuery({
    queryKey: ["coleta-indices"],
    queryFn: () =>
      api<{ indices: IndiceFiscal[]; alertas: string[] }>("/coleta/indices"),
    staleTime: 60_000,
  });
}

export function useExecucoesColeta() {
  return useQuery({
    queryKey: ["coleta-execucoes"],
    queryFn: () => api<{ execucoes: ExecucaoColeta[] }>("/coleta/execucoes"),
    staleTime: 30_000,
  });
}

/** Fila de aprovação agrupada por extração: um ato lido = um lote. */
export function useLotesPendentes() {
  return useQuery({
    queryKey: ["coleta-lotes"],
    queryFn: () => api<{ lotes: LotePropostas[] }>("/coleta/lotes"),
  });
}

/** Lê o ato da URL (Ato COTEPE/PMPF ou Convênio ICMS do ad rem) e enfileira as propostas. */
export function useExtrairAto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ url, tipo }: { url: string; tipo: TipoAto }) =>
      api<ExtracaoAto>("/coleta/indices/extrair", {
        method: "POST",
        body: { url, tipo },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["coleta-indices"] });
      qc.invalidateQueries({ queryKey: ["coleta-execucoes"] });
      qc.invalidateQueries({ queryKey: ["coleta-lotes"] });
    },
  });
}

/** Aplica ou rejeita o lote inteiro (tudo ou nada no TOML). */
export function useDecidirLote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      execucaoId,
      acao,
      motivo,
    }: {
      execucaoId: string;
      acao: "aprovar" | "rejeitar";
      motivo?: string;
    }) =>
      api(`/coleta/lotes/${execucaoId}/${acao}`, {
        method: "POST",
        body: { motivo },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["coleta-lotes"] });
      qc.invalidateQueries({ queryKey: ["coleta-indices"] });
    },
  });
}
